import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { decommissionRegister, getRegister } from '../../../../utils/service-calls/fiskaly';
import { ButtonBlack, ButtonRed } from '../../../reusable';

const InfoRow = ({ label, value }) => (
  <article className="ncss-col-sm-6 va-sm-t mb2-sm">
    <p className="body-4" style={{ color: '#757575', margin: 0 }}>{label}</p>
    <p className="body-2" style={{ margin: 0, wordBreak: 'break-all' }}>
      {value === undefined || value === null || value === '' ? '—' : value}
    </p>
  </article>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

InfoRow.defaultProps = {
  value: '',
};

const RegisterInfo = ({
  userToken, register: initialRegister, onBack, onDecommissioned,
}) => {
  const { storeId, registerId } = initialRegister;
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [register, setRegister] = useState(initialRegister);
  const [decommissioningRegister, setDecommissioningRegister] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchRegister = useCallback(() => {
    setFetchError('');
    setIsLoading(true);
    return Promise.all([
      getRegister(userToken, storeId, registerId),
    ])
      .then(([registerRes]) => {
        setRegister(registerRes.body);
        return null;
      })
      .catch((err) => setFetchError(err.message || 'Failed to load register details'))
      .finally(() => setIsLoading(false));
  }, [userToken, storeId, registerId]);

  useEffect(() => {
    fetchRegister();
  }, [fetchRegister]);

  const handleDecommissionRegister = () => {
    setDecommissioningRegister(true);
    setErrorMessage('');
    return decommissionRegister(userToken, storeId, registerId)
      .then(() => {
        onDecommissioned(registerId);
        return null;
      })
      .catch((err) => {
        setErrorMessage(err.message || `Failed to decommission register ${registerId}`);
        setDecommissioningRegister(false);
      });
  };

  return (
    <main className="ncss-col-sm-12 ta-sm-l">
      <div
        className="mb2-sm"
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: '12px',
          justifyContent: 'space-between',
        }}
      >
        {!isLoading && <ButtonBlack label="Back to Store" onClick={onBack} />}
        {!isLoading && !fetchError && register.status !== 'DECOMMISSIONED' && (
          <ButtonRed
            confirmationMessage={`Do you really want to decommission register ${registerId}?`}
            isDisabled={decommissioningRegister}
            isLoading={decommissioningRegister}
            label="Decommission Register"
            onClick={handleDecommissionRegister}
          />
        )}
      </div>
      {errorMessage && (
        <p className="text-color-error ta-sm-c body-2 mt2-sm">
          {errorMessage}
        </p>
      )}
      {(() => {
        if (isLoading) return <Loading />;
        if (fetchError) return <section className="text-color-error body-2 mt2-sm">{fetchError}</section>;
        return (
          <section className="bg-primary-grey pt2-sm pb2-sm prl2-sm border">
            <header className="u-rounded bg-black va-sm-m pt2-sm pb2-sm mb2-sm">
              <span className="headline-3 pl3-sm text-color-primary-light">
                Register Info
              </span>
            </header>
            <div className="ncss-row">
              <InfoRow label="Store ID" value={register.storeId} />
              <InfoRow label="Register ID" value={register.registerId} />
              <InfoRow label="Fiscal Device ID" value={register.fiscalDeviceId} />
              <InfoRow label="Fiskaly Location ID" value={register.fiskalyLocationId} />
              <InfoRow label="Fiskaly State" value={register.fiskalyState} />
              <InfoRow label="Fiskaly Mode" value={register.fiskalyMode} />
              <InfoRow label="Status" value={register.status} />
              <InfoRow label="Provisioned By" value={register.provisionedBy} />
              <InfoRow label="Provisioned At" value={register.provisionedAt} />
              <InfoRow label="Decommissioned At" value={register.decommissionedAt} />
            </div>
          </section>
        );
      })()}
    </main>
  );
};

RegisterInfo.defaultProps = {
  onDecommissioned: () => null,
};

RegisterInfo.propTypes = {
  onBack: PropTypes.func.isRequired,
  onDecommissioned: PropTypes.func,
  register: PropTypes.shape({
    decommissionedAt: PropTypes.string,
    fiscalDeviceId: PropTypes.string,
    fiskalyLocationId: PropTypes.string,
    fiskalyMode: PropTypes.string,
    fiskalyState: PropTypes.string,
    provisionedAt: PropTypes.string,
    provisionedBy: PropTypes.string,
    registerId: PropTypes.string.isRequired,
    status: PropTypes.string,
    storeId: PropTypes.string.isRequired,
  }).isRequired,
  userToken: PropTypes.string.isRequired,
};

export default RegisterInfo;
